import { API_BASE_URL } from "@/config";
import { validateToken } from '@/utils/auth';

const SERVICE_NAMES = {
  DIRECTOR_SCREENING: 'Director Screening',
  CIPC_VERIFICATION: 'CIPC Verification',
  ENTERPRISE_CREDIT_CHECK: 'Enterprise Credit Check',
  CIPC_PLUS_SCORE: "CIPC Plus Score",
  ENTITY_DATA_CAPTURE: 'Entity Data Capture'
};

export default {
  name: 'ArrangeWorkflow',
  data: () => ({
    services: [],
    workflow: [],
    arranged_workflow: [],
    is_selected: false,
    order_saved: false,
    selectedService: null,
    selectedOrder: null,
    maxOrder: 0,
    workflow_name: ''
  }),

  computed: {
    workflow_id() {
      return this.$store.getters.get_workflow_id;
    },
    sorted_services() {
      return this.services;
    },
    availableOrders() {
      const selectedOrders = this.services
        .filter(service => service !== this.selectedService)
        .map(service => service.selected_order)
        .filter(order => order !== null);
      const allOrders = Array.from({ length: this.maxOrder }, (_, i) => i + 1);
      const currentOrder = this.selectedService ? this.selectedService.selected_order : null;
      
      // If selected service is Entity Data Capture, only allow order 1
      if (this.selectedService && 
          (this.selectedService.service_name === SERVICE_NAMES.ENTITY_DATA_CAPTURE || 
           this.selectedService.title === SERVICE_NAMES.ENTITY_DATA_CAPTURE)) {
        return allOrders.filter(order => order === 1 && (!selectedOrders.includes(order) || order === currentOrder));
      }
      
      // If Entity Data Capture exists and is at position 1, don't allow other services to use position 1
      const entityDataCapture = this.services.find(s => 
        (s.service_name === SERVICE_NAMES.ENTITY_DATA_CAPTURE || s.title === SERVICE_NAMES.ENTITY_DATA_CAPTURE) &&
        s.selected_order === 1
      );
      
      if (entityDataCapture && this.selectedService !== entityDataCapture) {
        return allOrders.filter(order =>
          order !== 1 && (!selectedOrders.includes(order) || order === currentOrder)
        );
      }
      
      return allOrders.filter(order =>
        !selectedOrders.includes(order) || order === currentOrder
      );
    }
  },

  methods: {
    get_workflow_information() {
      const TOKEN = localStorage.getItem('token');
      const GET_WORKFLOW_INFORMATION = `${API_BASE_URL}/verify_api/workflow_management_api/get_workflow_data_api/${this.workflow_id}`;
      
      $.ajax({
        url: GET_WORKFLOW_INFORMATION,
        method: 'GET',
        contentType: 'application/json',
        headers: {
          Authorization: `Token ${TOKEN}`
        },
        success: (response) => {
          if (response.workflow_data && response.workflow_data.length > 0) {
            const workflow = response.workflow_data[0];
            this.workflow_name = this.capitalize_first_letter(workflow.workflow_name || '');
          }
        },
        error: (error) => {
          let error_message = 'Error fetching workflow information';
          if (error.responseJSON && error.responseJSON.error) {
            error_message = error.responseJSON.error;
          }
          console.error('Error:', error_message);
        }
      });
    },

    capitalize_first_letter(string) {
      if (!string) return '';
      return string.charAt(0).toUpperCase() + string.slice(1);
    },

    update_background(service_workflow_id, checked) {
      const TOKEN = localStorage.getItem('token');
      const UPDATE_BACKGROUND_API = `${API_BASE_URL}/verify_api/workflow_management_api/update_background/`;

      var data = {
        is_background: checked,
        service_workflow_id: service_workflow_id
      }

      $.ajax({
        url: UPDATE_BACKGROUND_API,
        method: 'POST',
        contentType: 'application/json',
        headers: {
          Authorization: `Token ${TOKEN}`
        },
        data: JSON.stringify(data),
        success: (response) => {
          if (response.status !== 'success') {
            var message = response.message;
            Swal.fire({
              icon: 'error',
              title: 'Error',
              text: message,
              showConfirmButton: false,
              timer: 3000,
              timerProgressBar: true
            }).then(() => {
              window.location.reload();
            });
          }
          else {
            window.location.reload();
          }
        },
        error: (error) => {
          Swal.close();
          let error_message = 'There was an error updating the background of the service';
          if (error.responseJSON) {
            const ERROR_RESPONSE = error.responseJSON;
            if (ERROR_RESPONSE && ERROR_RESPONSE.error) {
              error_message = ERROR_RESPONSE.error;
            }
          }
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: error_message,
            confirmButtonText: 'OK'
          });
        }
      });
    },

    get_selected_services_for_workflow() {
      const TOKEN = localStorage.getItem('token');
      const GET_SELECTED_SERVICES_FOR_WORKFLOW_API = `${API_BASE_URL}/verify_api/workflow_management_api/get_workflow_services/${this.workflow_id}`;

      $.ajax({
        url: GET_SELECTED_SERVICES_FOR_WORKFLOW_API,
        method: 'GET',
        contentType: 'application/json',
        headers: {
          Authorization: `Token ${TOKEN}`
        },
        beforeSend: () => {
          Swal.fire({
            text: 'Loading ...',
            allowEscapeKey: false,
            allowOutsideClick: false,
            didOpen: () => {
              Swal.showLoading();
            }
          });
        },
        success: (response) => {
          Swal.close();
          if (response.status === 'success' && Array.isArray(response.data)) {
            // Map through the response data
            this.services = response.data.map(item => {
              const serviceData = {
                ...((item.service || item.form) || {}),
                type: item.service ? 'service' : 'form',
                service_id: item.project_service_id,
                count: response.data.length,
                service_order: item.service_order || null,
                selected_order: item.service_order || null,
                is_background: item.is_background,
                workflow_project_service_id: item.workflow_project_service_id
              };
              return serviceData;
            });
            this.maxOrder = this.services.length;
            
            // Auto-assign Entity Data Capture to position 1 if it exists
            const entityDataCapture = this.services.find(s => 
              s.service_name === SERVICE_NAMES.ENTITY_DATA_CAPTURE || 
              s.title === SERVICE_NAMES.ENTITY_DATA_CAPTURE
            );
            
            if (entityDataCapture && !entityDataCapture.service_order) {
              entityDataCapture.service_order = 1;
              entityDataCapture.selected_order = 1;
              this.save_order_of_service(entityDataCapture).then(() => {
                this.get_saved_order_of_service();
              });
            } else if (this.services.length === 1 && !this.services[0].service_order) {
              this.services[0].service_order = 1;
              this.services[0].selected_order = 1;
              this.save_order_of_service(this.services[0]).then(() => {
              });
            } else {
              this.get_saved_order_of_service();
            }
          }
        },
        error: (error) => {
          Swal.close();
          let error_message = 'There was an error fetching selected services';
          if (error.responseJSON) {
            const ERROR_RESPONSE = error.responseJSON;
            if (ERROR_RESPONSE && ERROR_RESPONSE.error) {
              error_message = ERROR_RESPONSE.error;
            }
          }
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: error_message,
            confirmButtonText: 'OK'
          });
        }
      });
    },

    get_saved_order_of_service() {
      const TOKEN = localStorage.getItem('token');
      const GET_SAVED_ORDER_API = `${API_BASE_URL}/verify_api/workflow_management_api/get_service_order/${this.workflow_id}`;

      $.ajax({
        url: GET_SAVED_ORDER_API,
        method: 'GET',
        contentType: 'application/json',
        headers: {
          Authorization: `Token ${TOKEN}`
        },
        success: (response) => {
          if (response.status === 'success' && Array.isArray(response.data)) {
            response.data.forEach(savedService => {
              const matched_service = this.services.find(service => service.service_id === savedService.project_service_id);
              if (matched_service) {
                matched_service.service_order = savedService.service_order;
                matched_service.selected_order = savedService.service_order; // Update display order
              }
            });
          }
        },
        error: (error) => {
          Swal.close();
          let error_message = 'There was an error getting the saved order of services';
          if (error.responseJSON) {
            const ERROR_RESPONSE = error.responseJSON;
            if (ERROR_RESPONSE && ERROR_RESPONSE.error) {
              error_message = ERROR_RESPONSE.error;
            }
          }
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: error_message,
            confirmButtonText: 'OK'
          });
        }
      });
    },

    open_modal(service) {
      this.selectedService = service;
      // If the service already has an order, use it as initial value
      if (service.selected_order) {
        this.selectedOrder = service.selected_order;
      } else {
        // Find the lowest available order number
        const lowestAvailable = this.availableOrders[0];
        this.selectedOrder = lowestAvailable || null;
      }

      if (this.availableOrders.length === 0 && !service.selected_order) {
        Swal.fire({
          icon: 'warning',
          title: 'No Available Orders',
          text: 'All order positions are currently assigned. Please free up an order position first.',
          confirmButtonText: 'OK'
        });
        return;
      }

      // Show modal
      const modal = document.getElementById('yourModal');
      if (modal) {
        modal.classList.add('show');
        modal.setAttribute('aria-hidden', 'false');
        modal.style.display = 'block';
      }
    },

    close_modal() {
      this.selectedService = null;
      this.selectedOrder = null;
      const modal = document.getElementById('yourModal');
      if (modal) {
        modal.classList.remove('show');
        modal.setAttribute('aria-hidden', 'true');
        modal.style.display = 'none';
      }
    },

    save_order() {
      // Validate Entity Data Capture can only be position 1
      const serviceName = this.selectedService.service_name;
      
      if (serviceName === SERVICE_NAMES.ENTITY_DATA_CAPTURE && this.selectedOrder !== 1) {
        Swal.fire({
          icon: 'error',
          title: 'Invalid Order',
          text: 'Entity Data Capture can only be placed as the first service in the workflow.',
          confirmButtonText: 'OK'
        });
        return;
      }
      
      // Check if trying to place another service at position 1 when Entity Data Capture exists
      if (this.selectedOrder === 1 && serviceName !== SERVICE_NAMES.ENTITY_DATA_CAPTURE) {
        const entityDataCapture = this.services.find(s => 
          (s.service_name === SERVICE_NAMES.ENTITY_DATA_CAPTURE || s.title === SERVICE_NAMES.ENTITY_DATA_CAPTURE)
        );
        
        if (entityDataCapture) {
          Swal.fire({
            icon: 'error',
            title: 'Invalid Order',
            text: 'Position 1 is reserved for Entity Data Capture. Please select a different position.',
            confirmButtonText: 'OK'
          });
          return;
        }
      }
      
      // Prevent duplicate orders
      if (this.selectedService) {
        this.selectedService.service_order = this.selectedOrder;
        this.selectedService.selected_order = this.selectedOrder;
        this.save_order_of_service(this.selectedService);
        this.services = [...this.services];
        this.order_saved = true;
        this.close_modal();
      }
    },

    async save_order_of_service(service) {
      const TOKEN = localStorage.getItem('token');
      const BASE_URL = API_BASE_URL;
      const ENDPOINTS = {
        ARRANGE_WORKFLOW: `${BASE_URL}/verify_api/workflow_management_api/arrange_workflow_api/${this.workflow_id}`,
        CHECK_STATUS: `${BASE_URL}/verify_api/workflow_management_api/get_workflow_status/${this.workflow_id}`,
        UPDATE_STATUS: `${BASE_URL}/verify_api/workflow_management_api/edit_workflow_api/`
      };

      try {
        // First check the workflow status
        const statusResponse = await $.ajax({
          url: ENDPOINTS.CHECK_STATUS,
          method: 'GET',
          headers: {
            Authorization: `Token ${TOKEN}`
          }
        });

        let showSuccessMessage = false; // Default to not showing success message

        // If workflow is approved, show confirmation dialog
        if (statusResponse.status === 'approved') {
          const result = await Swal.fire({
            title: 'Warning',
            text: 'This workflow is currently approved. Making changes will set it back to incomplete status. Do you want to continue?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, continue',
            cancelButtonText: 'No, cancel',
            cancelButtonColor: '#d33'
          });

          if (!result.isConfirmed) {
            // If user cancels, refresh the service order to original state
            this.get_saved_order_of_service();
            return;
          }

          showSuccessMessage = true; // Show success message only when confirming status change

          // Update status to incomplete if user confirms
          await $.ajax({
            url: ENDPOINTS.UPDATE_STATUS,
            method: 'POST',
            contentType: 'application/json',
            data: JSON.stringify({
              workflow_id: this.workflow_id,
              status: 'incomplete'
            }),
            headers: {
              Authorization: `Token ${TOKEN}`
            }
          });
        }

        // Proceed with saving the service order
        const FORM_DATA = {
          service_id: service.service_id,
          service_order: service.service_order
        };

        await $.ajax({
          url: ENDPOINTS.ARRANGE_WORKFLOW,
          method: 'POST',
          contentType: 'application/json',
          headers: {
            Authorization: `Token ${TOKEN}`
          },
          data: JSON.stringify(FORM_DATA)
        });

        // Refresh the service order
        await this.get_saved_order_of_service();

        // Show success message only when confirming status change from approved
        if (showSuccessMessage) {
          await Swal.fire({
            icon: 'success',
            title: 'Success',
            text: 'Service order updated successfully',
            timer: 1500,
            showConfirmButton: false
          });
        }

      } catch (error) {
        Swal.close();
        let error_message = 'There was an error saving the order of service';
        if (error.responseJSON) {
          const ERROR_RESPONSE = error.responseJSON;
          if (ERROR_RESPONSE && ERROR_RESPONSE.error) {
            error_message = ERROR_RESPONSE.error;
          }
        }

        await Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error_message,
          confirmButtonText: 'OK'
        });

        // Refresh the service order on error to ensure UI is in sync
        this.get_saved_order_of_service();
      }
    },

    validate_director_screening_dependencies() {
      const directorScreening = this.services.find(service =>
        service.service_name === SERVICE_NAMES.DIRECTOR_SCREENING ||
        service.title === SERVICE_NAMES.DIRECTOR_SCREENING
      );

      if (!directorScreening) return true;

      const cipcVerification = this.services.find(service =>
        service.service_name === SERVICE_NAMES.CIPC_VERIFICATION ||
        service.title === SERVICE_NAMES.CIPC_VERIFICATION
      );

      const enterpriseCreditCheck = this.services.find(service =>
        service.service_name === SERVICE_NAMES.ENTERPRISE_CREDIT_CHECK ||
        service.title === SERVICE_NAMES.ENTERPRISE_CREDIT_CHECK
      );

      const cipcPlusScore = this.services.find(service =>
        service.service_name === SERVICE_NAMES.CIPC_PLUS_SCORE ||
        service.title === SERVICE_NAMES.CIPC_PLUS_SCORE
      );

      if (!cipcVerification && !enterpriseCreditCheck && !cipcPlusScore) {
        Swal.fire({
          icon: 'error',
          title: 'Missing Required Services',
          html: `"${SERVICE_NAMES.DIRECTOR_SCREENING}" requires either:<br>
             - ${SERVICE_NAMES.CIPC_VERIFICATION}<br>
             - ${SERVICE_NAMES.ENTERPRISE_CREDIT_CHECK}<br>
             - ${SERVICE_NAMES.CIPC_PLUS_SCORE}<br><br>
             Please add one of these services to your workflow.`,
          confirmButtonText: 'OK'
        });
        return false;
      }

      // Get Director Screening order
      const directorOrder = directorScreening.service_order;

      // Check if at least one dependency is before Director Screening
      const hasValidDependency =
        (cipcVerification && cipcVerification.service_order < directorOrder) ||
        (cipcPlusScore && cipcPlusScore.service_order < directorOrder) ||
        (enterpriseCreditCheck && enterpriseCreditCheck.service_order < directorOrder);

      if (!hasValidDependency) {
        // Build error message with details about the invalid services
        const invalidServices = [];
        if (cipcVerification && cipcVerification.service_order >= directorOrder) {
          invalidServices.push(`${SERVICE_NAMES.CIPC_VERIFICATION} (order ${cipcVerification.service_order})`);
        }
        if (cipcPlusScore && cipcPlusScore.service_order >= directorOrder) {
          invalidServices.push(`${SERVICE_NAMES.ENTERPRISE_CREDIT_CHECK} (order ${cipcPlusScore.service_order})`);
        }
        if (enterpriseCreditCheck && enterpriseCreditCheck.service_order >= directorOrder) {
          invalidServices.push(`${SERVICE_NAMES.ENTERPRISE_CREDIT_CHECK} (order ${enterpriseCreditCheck.service_order})`);
        }

        Swal.fire({
          icon: 'error',
          title: 'Service Order Error',
          html: `"${SERVICE_NAMES.DIRECTOR_SCREENING}" requires at least one service to run BEFORE it.<br><br>
             Current order issues:<br>
             - Director Screening is order ${directorOrder}<br>
             ${invalidServices.map(s => `- ${s} must be BEFORE`).join('<br>')}<br><br>
             Please reorder the services.`,
          confirmButtonText: 'OK'
        });
        return false;
      }

      return true;
    },

    validate_order_selection() {
      const orders = this.services.map(service => service.service_order).filter(order => order !== null);
      const unique_orders = new Set(orders);

      if (orders.length !== unique_orders.size) {
        Swal.fire({
          icon: 'error',
          title: 'Duplicate Orders',
          text: 'Please ensure all services have a unique order number.',
          confirmButtonText: 'OK'
        });
        return false;
      }

      if (orders.length !== this.services.length) {
        Swal.fire({
          icon: 'error',
          title: 'Incomplete Order',
          text: 'All services must have an assigned order before proceeding.',
          confirmButtonText: 'OK'
        });
        return false;
      }

      // New Director Screening validation
      if (!this.validate_director_screening_dependencies()) {
        return false;
      }

      return true;
    },

    route_to() {
      if (this.validate_order_selection()) {
        fetch(`${API_BASE_URL}/verify_api/workflow_management_api/check_workflow_background_order/${this.workflow_id}`, {
          method: 'GET',
          headers: {
            Authorization: `Token ${localStorage.getItem('token')}`
          }
        })
          .then(response => response.json())
          .then(response => {
            if (response.data === true) {
              this.$router.push({ name: 'upload_consent' });
            } else {
              Swal.fire({
                icon: 'warning',
                title: 'Service Order does not support the selected background service.',
                text: 'Please ensure that the needed prepopulated data is available',
                showConfirmButton: false,
                timer: 3500,
                timerProgressBar: true
              });
            }
          });
      }
    }
  },

  mounted() {
    if (!validateToken(this.$router)) {
      return;
    }
    this.get_workflow_information();
    this.get_selected_services_for_workflow();
  }
};